import React, { PureComponent } from "react";
import { KModal } from "../../../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../../../ui-kit/k-modal/k-modal-footer";
import { type IKuikaButtonStatusLabels } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaCollapsePanel from "../../../../ui-kit/kuika-collapse-panel/kuika-collapse-panel";
import KuikaModalIconWrapper from "../../../../ui-kit/kuika-modal-icon-wrapper/kuika-modal-icon-wrapper";
import KuikaH5 from "../../../../ui-kit/typography/H5/H5";
import KuikaSmallText from "../../../../ui-kit/typography/SmallText/SmallText";
import KuikaText from "../../../../ui-kit/typography/Text/Text";
import "./mcp-chat-rewind-dialog.scss";
import { formatDdlOp, formatSummaryLines, type RewindSummary, type RewindSummaryLine } from "./mcp-chat-rewind";

/** The revert says what it is doing on its own button; the dialog closes once it is through. */
const REWIND_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Reverting" };

interface McpChatRewindDialogProps {
  summary: RewindSummary;
  isLoadingSummary: boolean;
  isInFlight: boolean;
  errorText: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Modal dialog shown before an AI-chat revert executes. Surfaces exactly
 * what will be reverted, deleted, and kept (DDL warning) so the user can
 * abort if the scope looks wrong.
 *
 * Label convention: user-facing strings say "Revert", internal code uses
 * "rewind" (handler names, CSS classes, state keys) — the former was renamed
 * to "Revert" after product review but changing code paths would churn every
 * import site for no behavior gain.
 */
export default class McpChatRewindDialog extends PureComponent<McpChatRewindDialogProps> {
  componentDidMount(): void {
    document.addEventListener("keydown", this.handleKeyDown);
  }

  componentWillUnmount(): void {
    document.removeEventListener("keydown", this.handleKeyDown);
  }

  private handleKeyDown = (e: KeyboardEvent): void => {
    if (this.props.isInFlight) return;
    if (e.key === "Escape") {
      e.preventDefault();
      this.props.onCancel();
    }
  };

  private buildCollapseItems = (
    lines: RewindSummaryLine[]
  ): Array<{ key: string; label: React.ReactNode; children: React.ReactNode }> => {
    // Only lines that carry entity names become collapsible. The conversation
    // line is just a flat count and renders as an inline summary row below.
    return lines
      .filter((line) => line.kind !== "conversation" && line.names.length > 0)
      .map((line, i) => ({
        key: `line-${i}`,
        label: (
          <span className={`mcp-rewind-modal__line-headline mcp-rewind-modal__line-headline--${line.kind}`}>
            {line.headline}
          </span>
        ),
        children: (
          <ul className="mcp-rewind-modal__name-list">
            {line.names.map((name, j) => (
              <li key={j} className="mcp-rewind-modal__name-item" title={name}>
                {name}
              </li>
            ))}
          </ul>
        )
      }));
  };

  render(): JSX.Element {
    const { summary, isLoadingSummary, isInFlight, errorText, onConfirm, onCancel } = this.props;
    const lines = isLoadingSummary ? [] : formatSummaryLines(summary);
    const conversationLine = lines.find((l) => l.kind === "conversation") || null;
    const collapseItems = this.buildCollapseItems(lines);
    const hasDdl = !isLoadingSummary && summary.ddlOps.length > 0;
    const disableConfirm = isInFlight || isLoadingSummary || summary.isEmpty;

    const footer = (
      <KModalFooter
        onCancel={onCancel}
        cancelTestId="rewindCancelButton"
        submitLabel="Revert"
        submitTone="danger"
        submitTestId="rewindConfirmButton"
        onSubmit={onConfirm}
        status={isInFlight ? "loading" : undefined}
        statusLabels={REWIND_STATUS_LABELS}
        disabled={disableConfirm}
      />
    );

    return (
      <KModal
        showModal={true}
        closeModal={isInFlight ? () => {} : onCancel}
        header="Revert to this message"
        footer={footer}
        width={480}
        centered
        isLoading={isLoadingSummary}
      >
        <div className="mcp-rewind-modal">
          <div className="mcp-rewind-modal__header">
            <KuikaModalIconWrapper iconName="arrow-back-up-x" intent="destructive" />
            <div className="mcp-rewind-modal__title">
              <KuikaH5>Revert to this message</KuikaH5>
              <KuikaText className="mcp-rewind-modal__subtitle">
                Review the changes that will be rolled back before confirming
              </KuikaText>
            </div>
          </div>

          <div className="mcp-rewind-modal__content">
            {isLoadingSummary ? (
              // Placeholder keeps the modal at a stable height while the KModal-
              // level spinner overlay does the actual busy indication.
              <div className="mcp-rewind-modal__loading-placeholder" aria-hidden />
            ) : summary.isEmpty ? (
              <KuikaText className="mcp-rewind-modal__empty">
                Nothing to revert — no changes were made after this message.
              </KuikaText>
            ) : (
              <>
                {collapseItems.length > 0 && <KuikaCollapsePanel items={collapseItems} expandIconPosition="right" />}

                {conversationLine && (
                  <div className="mcp-rewind-modal__conversation-line">{conversationLine.headline}</div>
                )}

                {hasDdl && (
                  <div className="mcp-rewind-modal__ddl">
                    <div className="mcp-rewind-modal__ddl-title">Not reverted (permanent)</div>
                    <ul className="mcp-rewind-modal__ddl-list">
                      {summary.ddlOps.map((op, i) => (
                        <li key={i}>{formatDdlOp(op)}</li>
                      ))}
                    </ul>
                    <KuikaSmallText className="mcp-rewind-modal__ddl-hint">
                      Schema changes persist after revert. Drop or alter these manually if needed.
                    </KuikaSmallText>
                  </div>
                )}
              </>
            )}

            {errorText && <div className="mcp-rewind-modal__error">{errorText}</div>}
          </div>
        </div>
      </KModal>
    );
  }
}
